import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { File as ProtoFile } from '@common/entities';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

export type FileAction = 'create' | 'update' | 'delete' | 'noAction' | null;

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private http = inject(HttpClient);

  public upload(blobData: Blob, extension: string): Promise<ProtoFile> {
    const formData = new FormData();

    formData.append('file', blobData, `myimage.${extension}`);

    return this.http.post<ProtoFile>(ProtoUrl.filesUpload(), formData)
      .toPromise();
  }

  public uploadFromUrl(fileUrl: string): Promise<ProtoFile> {
    return this.http.post<ProtoFile>(ProtoUrl.filesUploadFromUrl(), {
      url: fileUrl,
    })
      .toPromise();
  }

  public delete(fileId: string): Promise<void> {
    return this.http.delete<void>(ProtoUrl.filesById(fileId))
      .toPromise();
  }

  public async manageFiles(oldFile?: ProtoFile, newFile?: ProtoFile | File): Promise<string | null> {
    const action = this.getFileAction(oldFile, newFile);

    if (!action) {
      console.error('[OrganizationService] Could not get the file action. That should not happen!');
      throw new Error('[OrganizationService] Could not get the file action. That should not happen!');
    }

    try {
      switch (action) {
        case 'create': {
          const { id: fileId } = await this.upload(newFile as File, (newFile as File).type);

          return fileId;
        }

        case 'update': {
          const { id: fileId } = await this.upload(newFile as File, (newFile as File).type);

          this.delete(oldFile.id).catch((error) => {
            console.error('[OrganizationService] Error deleting old file:', error);
          });

          return fileId;
        }

        case 'delete':
          // By deleting the file here, we can no longer determine whether the previous state of the file
          // deviates from the current state in the backend.
          // So just setting null here, deletion needs to happen in the backend.
          return null;

        default:
          // this is the 'noAction' case --> we either have no files or they are the same (check out getFileAction())
          if (!oldFile && !newFile) {
            return null;
          }

          return (newFile as ProtoFile).id;
      }
    } catch (error) {
      console.error('[OrganizationService] Error handling organization files. action:', action, ' - error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public getFileAction(oldFile?: ProtoFile, newFile?: ProtoFile | File): FileAction {
    if (!oldFile && !newFile) {
      return 'noAction'; // no file to handle
    }

    if (!oldFile) {
      return 'create'; // new file to create
    }

    if (!newFile) {
      return 'delete'; // old file to delete
    }

    if (newFile instanceof File) {
      // new File is not a ProtoFile --> it was not uploaded before (so it's new)
      return 'update'; // we have an old, uploaded file and a new, not yet uploaded file
    }

    // newFile is a ProtoFile, so it was uploaded before
    if (oldFile.id === (newFile as ProtoFile).id) {
      return 'noAction'; // no change, same file
    }

    // this should not happen, but just in case
    console.warn('[OrganizationService] getFileAction called but no action defined for oldFile:', oldFile, 'newFile:', newFile);

    return null; // error case, no action defined
  }
}
